import React, { useEffect, useState } from 'react';

import { RemoveVehicleConfirmationLoading } from '../../remove-vehicle-confirmation-loading';
import { RemoveVehicleConfirmationView } from '../../remove-vehicle-confirmation/RemoveVehicleConfirmationView';
import { RemoveVehicleFormView } from '../../remove-vehicle-form/RemoveVehicleFormView';
import { Vehicles } from '../../../../../shared/typings/main';
import { onVWBasicStepLoadEvent, onVWBasicLayerLoadEvent } from '../../../../../shared/tagging/tagging-helpers';
import { useFeatureServices } from '../../../../../shared/hooks/use-feature-services';
import { useMyVwContext } from '../../../../../shared/hooks/use-my-vw-context';
import { useToast } from '../../../../../shared/context/ToastContext';
import { useTrackingManager } from '../../../../../shared/hooks/use-tracking-manager';
import { useUser } from '../../../../../app/providers/UserContext';
import { useVehicle } from '../../../../../app/providers/VehicleContext';

export enum RemoveVehicleStep {
  info = 1,
  confirmation = 2,
  loading = 3,
}

export const useRemoveVehicleLayerData = (active: boolean, requestClose: () => void) => {
  const { translationHandler, authService } = useMyVwContext();
  const { user } = useUser();
  const [step, setStep] = useState<RemoveVehicleStep>(RemoveVehicleStep.info);
  const { showToast } = useToast();
  const { userPreferredVehicle, removePreferredVehicleUIonly } = useVehicle();
  const trackingManager = useTrackingManager();
  const { 'locale-service': localeService } = useFeatureServices();
  const localeCode = `${localeService.language}-${localeService.countryCode}`;
  const isCanada = localeService?.countryCode === 'CA';

  // Track interaction layer load when it becomes visible
  useEffect(() => {
    if (active && step === RemoveVehicleStep.info) {
      try {
        if (isCanada) {
          onVWBasicLayerLoadEvent(trackingManager, 'Vehicle Details - Remove Vehicle');
        } else {
          onVWBasicStepLoadEvent(trackingManager, {
            Name: 'Vehicle Management',
            StepName: 'Vehicle Details - Remove Vehicle',
            StepNumber: '5.2',
          });
        }
      } catch (error) {
        console.warn('Error tracking remove vehicle interaction layer load:', error);
      }
    }
  }, [active, step, trackingManager]);

  useEffect(() => {
    if (!active) setStep(RemoveVehicleStep.info);
  }, [active]);

  const handleShowLayer = () => {
    requestClose();
    setStep(RemoveVehicleStep.info);
  };

  const goToConfirmation = () => {
    setStep(RemoveVehicleStep.confirmation);
  };

  const handleConfirm = async () => {
    setStep(RemoveVehicleStep.loading);

    try {
      const userId = user?.id || user?.vwId;

      if (!userId) {
        console.error('Unable to remove vehicle: User ID not found');
        showToast(
          translationHandler(
            'vehicleManagement.deactivate.removeVehicle.error.message',
          ).replace(
            'year_model',
            `${userPreferredVehicle?.modelYear || ''} ${userPreferredVehicle?.modelName || ''}`,
          ),
          'Alert',
        );
        handleShowLayer();
        return;
      }

      // * For local Only - use "test-fail-user" to fail the local tests
      // const response =
      //   await authService.mockRemoveVehicleUserAssociation('test');
      // * For Integration and Prod
      const response =
        await authService.removeVehicleUserAssociationGqlRequest(userId, localeCode);

      if (response?.removeVehicleUserAssociation?.status === 'Success') {
        await removePreferredVehicleUIonly();
        showToast(
          translationHandler(
            'vehicleManagement.deactivate.removeVehicle.success.message',
          ).replace(
            'year_model',
            `${userPreferredVehicle?.modelYear || ''} ${userPreferredVehicle?.modelName || ''}`,
          ),
          'Success',
        );
        handleShowLayer();
      }
    } catch (error: any) {
      console.error('Error removing vehicle:', error);
      showToast(
        translationHandler(
          'vehicleManagement.deactivate.removeVehicle.error.message',
        ).replace(
          'year_model',
          `${userPreferredVehicle?.modelYear || ''} ${userPreferredVehicle?.modelName || ''}`,
        ),
        'Alert',
      );
      handleShowLayer();
    }
  };

  const vehicles = user?.vehicles || [];
  const preferredVehicle = vehicles.length > 1
    ? vehicles.find((v: Vehicles) => v.preferred)
    : vehicles[0];
  const userVehicles: Vehicles[] = preferredVehicle ? [preferredVehicle] : [];

  let title = '';
  let content = <></>;

  switch (step) {
    case RemoveVehicleStep.info:
      title = translationHandler('vehicleManagement.removeVehicle.title');
      content = (
        <RemoveVehicleFormView
          translationHandler={translationHandler}
          trackingManager={trackingManager}
          userVehicles={userVehicles}
          userRole={user?.userRole}
          isCanada={isCanada}
          goToConfirmation={goToConfirmation}
          onGoBack={handleShowLayer}
        />
      );
      break;

    case RemoveVehicleStep.confirmation:
      title = translationHandler('vehicleManagement.removeVehicle.confirmation.title');
      content = (
        <RemoveVehicleConfirmationView
          translationHandler={translationHandler}
          trackingManager={trackingManager}
          userRole={user?.userRole}
          vehicleModelYear={userPreferredVehicle?.modelYear}
          vehicleModelName={userPreferredVehicle?.modelName}
          handleConfirm={handleConfirm}
          goBack={handleShowLayer}
        />
      );
      break;

    case RemoveVehicleStep.loading:
      title = '';
      content = <RemoveVehicleConfirmationLoading />;
      break;
  }

  return {
    title,
    content,
    isDeactivating: step === RemoveVehicleStep.loading,
    step,
  };
};